import { Directive, ElementRef, forwardRef, Input, Optional } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidatorFn, Validators } from '@angular/forms';
import { ThemePalette } from '@ngx-simple/core/common-behaviors';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { SimFormFieldComponent } from '@ngx-simple/simple-ui/form-field';
import { SIM_INPUT_VALUE_ACCESSOR } from '@ngx-simple/simple-ui/input';
import { SimDatepickerControl } from './datepicker-base';
import { DateFilterFn, SimDatepickerInputBase } from './datepicker-input-base';
import { SimDatepickerComponent } from './datepicker.component';

@Directive({
  selector: 'input[simDatepicker]',
  exportAs: 'simDatepickerInput',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SimDatepickerInputDirective),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SimDatepickerInputDirective),
      multi: true
    },
    { provide: SIM_INPUT_VALUE_ACCESSOR, useExisting: SimDatepickerInputDirective }
  ],
  host: {
    class: 'sim-datepicker-input',
    '[attr.min]': 'min ? _dateAdapter.toIso8601(min) : null',
    '[attr.max]': 'max ? _dateAdapter.toIso8601(max) : null'
  }
})
export class SimDatepickerInputDirective<D> extends SimDatepickerInputBase<D | null, D> implements SimDatepickerControl<D | null> {
  /** 格式化input显示的时间字符串样式 */
  @Input() format: string;

  /**
   * 选择面板 决定 startView 值
   * - year 选择多年面板 => multi-year
   * - month 选择月份面板 => multi-year | year
   * - week 选择周面板 => multi-year | year | month
   * - date 选择日期面板 => multi-year | year | month
   * - datetime 选择日期时间面板 => multi-year | year | month | date
   */
  @Input() picker: 'year' | 'month' | 'week' | 'date' | 'datetime' = 'date';

  /** 与此输入相关联的时间选择器 */
  @Input()
  set simDatepicker(datepicker: SimDatepickerComponent<D>) {
    if (datepicker) {
      this._datepicker = datepicker;
      this._registerModel(datepicker._registerInput(this));
    }
  }
  _datepicker: SimDatepickerComponent<D>;

  /** 最小有效日期 */
  @Input()
  get min(): D | null {
    return this._min;
  }
  set min(value: D | null) {
    const validValue = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));

    if (!this._dateAdapter.sameDate(validValue, this._min)) {
      this._min = validValue;
      this._validatorOnChange();
    }
  }
  private _min: D | null;

  /** 最大有效日期 */
  @Input()
  get max(): D | null {
    return this._max;
  }
  set max(value: D | null) {
    const validValue = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));

    if (!this._dateAdapter.sameDate(validValue, this._max)) {
      this._max = validValue;
      this._validatorOnChange();
    }
  }
  private _max: D | null;

  /** 可用于过滤时间选择器中日期的方法。 */
  @Input('simDatepickerFilter')
  get dateFilter() {
    return this._dateFilter;
  }
  set dateFilter(value: DateFilterFn<D | null>) {
    this._dateFilter = value;
    this._validatorOnChange();
  }
  private _dateFilter: DateFilterFn<D | null>;

  /** 此<input>的组合表单控制验证器。 */
  // Implemented as part of Validator.
  protected _validator: ValidatorFn | null;

  constructor(
    _elementRef: ElementRef<HTMLInputElement>,
    @Optional() _dateAdapter: SimDateAdapter<D>,
    @Optional() private _formField: SimFormFieldComponent
  ) {
    super(_elementRef, _dateAdapter);
    this._validator = Validators.compose(super._getValidators());
  }

  protected _openPopup(): void {
    if (this._datepicker) {
      this._datepicker.open();
    }
  }

  /**
   * 获取应该与datepicker弹出窗口连接的元素
   * @return 要连接弹出窗口的元素
   */
  getConnectedOverlayOrigin(): ElementRef {
    return this._formField ? this._formField._connectionContainerRef : this._elementRef;
  }

  /** 返回<input>的表单字段使用的调色板 */
  getThemePalette(): ThemePalette {
    return this._formField ? this._formField.color : undefined;
  }

  /** 获取日历应该开始的值 */
  getStartValue(): D | null {
    return this.value;
  }

  /** 获取输入的最小日期 */
  _getMinDate() {
    return this._min;
  }

  /** 获取输入的最大日期 */
  _getMaxDate() {
    return this._max;
  }

  protected _getValueFromModel(modelValue: D | null): D | null {
    return modelValue;
  }

  protected _assignValueToModel(value: D | null): void {
    if (this._model) {
      this._model.updateSelection(value, this);
    }
  }

  /** 获取输入的日期过滤方法 */
  protected _getDateFilter() {
    return this._dateFilter;
  }

  protected _canEmitChangeEvent() {
    return true;
  }

  // 选择单个日期时不必要
  protected _outsideValueChanged: undefined;
}
